<template>
  <div>
    <div class="py-3"></div>
    <v-card class="mx-auto" max-width="95%">
      <v-overlay :value="loading" absolute opacity="0.7">
        <v-progress-circular indeterminate size="48" />
      </v-overlay>

      <v-toolbar color="light-blue darken-3" dark>
        <v-toolbar-title>쿠폰 상품 관리</v-toolbar-title>
        <v-spacer />
        <v-btn color="white" outlined @click="syncNow" :disabled="loading">
          <v-icon left>mdi-sync</v-icon>
          지금 동기화
        </v-btn>
      </v-toolbar>

      <!-- 필터바 -->
      <v-card-text class="pb-0">
        <v-row dense align="center">
          <v-col cols="12" md="4">
            <v-text-field
              v-model="keyword"
              label="상품명 검색"
              outlined
              dense
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
              @keyup.enter="loadProducts"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="categoryPid"
              :items="categoryItems"
              label="카테고리"
              outlined
              dense
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="daouStatus"
              :items="statusItems"
              label="다우 상태"
              outlined
              dense
              hide-details
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-btn color="primary" block :disabled="loading" @click="loadProducts">
              <v-icon left>mdi-magnify</v-icon>
              조회
            </v-btn>
          </v-col>
        </v-row>

        <div class="text-caption grey--text mt-3">
          <v-icon x-small color="grey">mdi-information-outline</v-icon>
          신규 상품은 노출 OFF 상태로 등록됩니다. 검수 후 노출을 켜주세요.
          회색 항목은 다우 동기화가 관리하므로 수정할 수 없습니다. (편집 가능: 판매가 / 노출)
        </div>
      </v-card-text>

      <v-card-text class="pa-0 pt-3">
        <v-data-table
          v-if="products.length > 0"
          :headers="headers"
          :items="products"
          :items-per-page="20"
          item-key="PID"
          class="elevation-0"
          :footer-props="{ 'items-per-page-options': [20, 50, 100, -1] }"
        >
          <template v-slot:item.GOODS_IMAGE="{ item }">
            <v-avatar size="48" rounded :style="rowStyle(item)">
              <v-img v-if="item.GOODS_IMAGE" :src="item.GOODS_IMAGE" contain />
              <v-icon v-else color="grey">mdi-image-off</v-icon>
            </v-avatar>
          </template>

          <template v-slot:item.PRODUCT_NAME="{ item }">
            <div :style="rowStyle(item)">
              <span class="font-weight-medium grey--text text--darken-2">
                {{ item.PRODUCT_NAME || '-' }}
              </span>
            </div>
          </template>

          <template v-slot:item.CATEGORY_NAME="{ item }">
            <span class="grey--text" :style="rowStyle(item)">
              {{ item.CATEGORY_NAME || item.CATEGORY || '-' }}
            </span>
          </template>

          <template v-slot:item.FACE_VALUE="{ item }">
            <span class="grey--text" :style="rowStyle(item)">{{ money(item.FACE_VALUE) }}</span>
          </template>

          <template v-slot:item.CPN_PRICE="{ item }">
            <span class="grey--text" :style="rowStyle(item)">{{ money(item.CPN_PRICE) }}</span>
          </template>

          <template v-slot:item.SALE_PRICE="{ item }">
            <v-text-field
              v-model="item.SALE_PRICE"
              type="number"
              dense
              hide-details
              outlined
              single-line
              style="max-width: 120px;"
              :disabled="loading"
              @change="onSalePriceChange(item)"
            />
          </template>

          <template v-slot:item.ACTIVE="{ item }">
            <v-switch
              :input-value="item.ACTIVE === 'Y'"
              :disabled="item.DAOU_STATUS === 'DELISTED' || loading"
              color="success"
              dense
              hide-details
              class="mt-0 pt-0"
              @change="onActiveChange(item, $event)"
            />
          </template>

          <template v-slot:item.DAOU_STATUS="{ item }">
            <v-chip
              x-small
              dark
              :color="item.DAOU_STATUS === 'AVAILABLE' ? 'success' : 'grey'"
            >
              {{ item.DAOU_STATUS === 'AVAILABLE' ? '판매중' : '판매중지' }}
            </v-chip>
          </template>
        </v-data-table>

        <v-container v-if="products.length === 0 && !loading" class="text-center py-8">
          <v-icon size="64" color="grey lighten-1">mdi-ticket-percent-outline</v-icon>
          <p class="text-body-1 grey--text mt-3">조회된 상품이 없습니다.</p>
        </v-container>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { coupon } from '../../api';

export default {
  name: 'Coupon_Products',
  data: () => ({
    loading: false,
    keyword: '',
    categoryPid: null,
    daouStatus: '',
    products: [],
    categories: [],
    statusItems: [
      { text: '전체', value: '' },
      { text: 'AVAILABLE (판매중)', value: 'AVAILABLE' },
      { text: 'DELISTED (판매중지)', value: 'DELISTED' },
    ],
    headers: [
      { text: '이미지', value: 'GOODS_IMAGE', width: '80', sortable: false },
      { text: '상품명', value: 'PRODUCT_NAME' },
      { text: '카테고리', value: 'CATEGORY_NAME', width: '140' },
      { text: '정가', value: 'FACE_VALUE', width: '110', align: 'end' },
      { text: '쿠폰가', value: 'CPN_PRICE', width: '110', align: 'end' },
      { text: '판매가', value: 'SALE_PRICE', width: '140', sortable: false },
      { text: '노출', value: 'ACTIVE', width: '90', sortable: false, align: 'center' },
      { text: '다우상태', value: 'DAOU_STATUS', width: '110', align: 'center' },
    ],
  }),
  computed: {
    categoryItems() {
      return this.categories.map((c) => ({
        text: c.DISPLAY_NAME || c.DAOU_CATEGORY,
        value: c.PID,
      }));
    },
  },
  mounted() {
    this.loadCategories();
    this.loadProducts();
  },
  methods: {
    money(v) {
      if (v === null || v === undefined || v === '') return '-';
      const n = Number(v);
      return Number.isNaN(n) ? String(v) : n.toLocaleString();
    },
    rowStyle(item) {
      return item.DAOU_STATUS === 'DELISTED' ? 'opacity: 0.5;' : '';
    },
    errText(e, fallback) {
      return typeof e === 'string' ? e : fallback;
    },
    loadCategories() {
      coupon.getCategories()
        .then((res) => {
          this.categories = (res && res.categories) || [];
        })
        .catch((e) => {
          alert(this.errText(e, '카테고리를 불러오지 못했습니다.'));
        });
    },
    loadProducts() {
      const params = {};
      const kw = (this.keyword || '').trim();
      if (kw) params.keyword = kw;
      if (this.categoryPid) params.category_pid = this.categoryPid;
      if (this.daouStatus) params.daou_status = this.daouStatus;

      this.loading = true;
      coupon.getProducts(params)
        .then((res) => {
          this.products = (res && res.products) || [];
        })
        .catch((e) => {
          alert(this.errText(e, '상품 목록을 불러오지 못했습니다.'));
        })
        .finally(() => { this.loading = false; });
    },
    onSalePriceChange(item) {
      const price = Number(item.SALE_PRICE);
      if (item.SALE_PRICE === '' || item.SALE_PRICE === null || Number.isNaN(price) || price < 0) {
        alert('판매가는 0 이상의 숫자로 입력해주세요.');
        this.loadProducts();
        return;
      }
      this.loading = true;
      coupon.updateProduct(item.PID, { sale_price: price })
        .then(() => { item.SALE_PRICE = price; })
        .catch((e) => {
          alert(this.errText(e, '판매가 저장에 실패했습니다.'));
          this.loadProducts();
        })
        .finally(() => { this.loading = false; });
    },
    onActiveChange(item, checked) {
      const next = checked ? 'Y' : 'N';
      if (next === item.ACTIVE) return;
      this.loading = true;
      coupon.updateProduct(item.PID, { active: next })
        .then(() => { item.ACTIVE = next; })
        .catch((e) => {
          alert(this.errText(e, '노출 설정 저장에 실패했습니다.'));
          this.loadProducts();
        })
        .finally(() => { this.loading = false; });
    },
    syncNow() {
      if (!confirm('다우 카탈로그를 지금 동기화합니다. 시간이 다소 걸릴 수 있습니다. 진행할까요?')) return;
      this.loading = true;
      coupon.syncNow()
        .then(() => {
          this.loadCategories();
          this.loadProducts();
        })
        .catch((e) => {
          alert(this.errText(e, '동기화에 실패했습니다.'));
          this.loading = false;
        });
    },
  },
};
</script>
